import {IApiClient} from '@lib/infrastructure';
import {
  CreateWorkdayDto,
  CreateWorkspaceReservationDto,
  GetReservationCountQuery,
  LocalDateTimeString,
  PatchWorkspaceReservationDto,
  ReservationCount,
  UpdateWorkdayDto,
  UTCDateTimeString,
  Workday,
  WorkspaceReservation,
  WorkspaceReservationWithOwner,
} from '../apiTypes';
import {LocalTimespan} from '../stateTypes';
import {ParkingDto, TEMP_constructOverviewOnTheClient} from './TEMP_constructOverviewOnTheClient';

type ReservationsQuery = LocalTimespan & {
  ownerId?: Array<string>;
  nodeId?: Array<string>;
};

export type Api = {
  // Core methods
  getWorkdays: (q: ReservationsQuery & {status?: Array<Workday['status']>}) => Promise<Array<Workday>>;
  getWorkday: (workdayId: string) => Promise<Workday>;
  postWorkday: (dto: CreateWorkdayDto) => Promise<Workday>;
  putWorkday: (dto: UpdateWorkdayDto) => Promise<Workday>;
  deleteWorkday: (workdayId: string) => Promise<void>;

  getWorkspaceReservations: (
    q: ReservationsQuery & {status?: Array<WorkspaceReservation['status']>},
  ) => Promise<Array<WorkspaceReservation>>;
  postWorkspaceReservation: (dto: CreateWorkspaceReservationDto) => Promise<WorkspaceReservation>;
  patchWorkspaceReservation: (dto: PatchWorkspaceReservationDto) => Promise<WorkspaceReservation>;

  getReservationOverviewExport: (
    q: {
      from: UTCDateTimeString;
      to: UTCDateTimeString;
    },
    parkingEnabled: boolean,
  ) => Promise<{data: string; filename: string}>;

  getReservationCounts: (q: GetReservationCountQuery) => Promise<Array<ReservationCount>>;
  getDeskReservationCounts: (q: GetReservationCountQuery & {areaId: string}) => Promise<Array<ReservationCount>>;

  // Extensions
  getWorkspaceReservation: (workspaceReservationId: string) => Promise<WorkspaceReservationWithOwner>;
  getWorkspaceReservationAttendees: (workspaceReservationId: string) => Promise<Array<WorkspaceReservationWithOwner>>;

  getMyWorkdays: (q: LocalTimespan) => Promise<Array<Workday>>;
  getMyConnectionWorkdays: (q: LocalTimespan) => Promise<Array<Workday>>;
  getMyWorkspaceReservations: (q: LocalTimespan) => Promise<Array<WorkspaceReservation>>;
  getMyConnectionWorkspaceReservations: (q: LocalTimespan) => Promise<Array<WorkspaceReservation>>;
};

export const createRealApi = (apiClient: IApiClient): Api => {
  function notImplemented(): never {
    throw new Error('NotImplemented');
  }

  // Api management
  const BASE_URL = '';

  const api: Api = {
    getWorkdays: async ({from, to}) => notImplemented(),
    getWorkday: async () => notImplemented(),

    getWorkspaceReservations: async () => [],

    postWorkspaceReservation: async (dto) => {
      return apiClient.request<WorkspaceReservation>({
        method: 'post',
        url: `${BASE_URL}/me/workspace-reservations`,
        data: dto,
      });
    },
    patchWorkspaceReservation: async (dto) => {
      const {id, ...changes} = dto;
      return apiClient.request<WorkspaceReservation>({
        method: 'patch',
        url: `${BASE_URL}/me/workspace-reservations/${id}`,
        data: changes,
      });
    },

    getWorkspaceReservation: async (workspaceReservationId) => {
      return apiClient.request<WorkspaceReservationWithOwner>({
        method: 'get',
        url: `${BASE_URL}/workspace-reservations/${workspaceReservationId}`,
      });
    },
    getWorkspaceReservationAttendees: async (workspaceReservationId) => {
      return apiClient.request<Array<WorkspaceReservationWithOwner>>({
        method: 'get',
        url: `${BASE_URL}/workspace-reservations/${workspaceReservationId}/attendees`,
      });
    },

    getReservationOverviewExport(q: {from: UTCDateTimeString; to: UTCDateTimeString}, parkingEnabled) {
      const reservationApi = this;

      const resolveParkings = parkingEnabled
        ? ({from, to}: {from: LocalDateTimeString; to: LocalDateTimeString}) =>
            apiClient.request<ParkingDto[]>({
              url: `/parking/me/reservations?startDate=${from}&endDate=${to}`,
              method: 'get',
            })
        : () => Promise.resolve([]);

      return TEMP_constructOverviewOnTheClient(new Date(q.from), new Date(q.to), reservationApi, resolveParkings);
    },

    getReservationCounts(params) {
      const q = new URLSearchParams();

      q.append('groupUpTo', 'Area');
      q.append('nodeId', params.nodeId);
      q.append('startDate', params.startDate);
      q.append('endDate', params.endDate);

      return apiClient.request<Array<ReservationCount>>({
        method: 'get',
        url: `${BASE_URL}/workspace-reservations/count?${q}`,
      });
    },

    getDeskReservationCounts(params) {
      const q = new URLSearchParams();

      q.append('groupUpTo', 'Desk');
      q.append('nodeId', params.nodeId);
      q.append('startDate', params.startDate);
      q.append('endDate', params.endDate);

      return apiClient.request<Array<ReservationCount>>({
        method: 'get',
        url: `${BASE_URL}/workspace-reservations/count?${q}`,
      });
    },

    postWorkday: async (dto) => {
      return apiClient.request<Workday>({
        method: 'post',
        url: `${BASE_URL}/me/workdays`,
        data: dto,
      });
    },

    putWorkday: async (dto) => {
      return apiClient.request<Workday>({
        method: 'put',
        url: `${BASE_URL}/me/workdays/${dto.id}`,
        data: dto,
      });
    },

    deleteWorkday: async (workdayId) => {
      await apiClient.request({
        method: 'delete',
        url: `${BASE_URL}/me/workdays/${workdayId}`,
      });
    },

    getMyWorkdays: async ({from, to}) => {
      return apiClient.request<Array<Workday>>({
        method: 'get',
        url: `${BASE_URL}/me/workdays?startDate=${from}&endDate=${to}`,
      });
    },
    getMyWorkspaceReservations: async ({from, to}) => {
      return apiClient.request<Array<WorkspaceReservation>>({
        method: 'get',
        url: `${BASE_URL}/me/workspace-reservations?startDate=${from}&endDate=${to}`,
      });
    },
    getMyConnectionWorkdays: async ({from, to}) => {
      return apiClient.request<Array<Workday>>({
        method: 'get',
        url: `${BASE_URL}/me/connections/workdays?startDate=${from}&endDate=${to}`,
      });
    },
    getMyConnectionWorkspaceReservations: async ({from, to}) => {
      return apiClient.request<Array<WorkspaceReservation>>({
        method: 'get',
        url: `${BASE_URL}/me/connections/workspace-reservations?startDate=${from}&endDate=${to}`,
      });
    },
  };

  return api;
};
